import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
} from '@mui/icons-material';
import { useConversationsListQuery } from '../hooks/useConversationsListQuery';
import { useConversationDetailQuery } from '../hooks/useConversationDetailQuery';
import { invalidateMessagingCaches } from '../lib/messagingQueryCache';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { getAnnouncementImageUrl } from '../utils/imageUrl';
import FoundObjectMessagesWorkspace from '../components/FoundObjectMessagesWorkspace';
import {
  getFoundObjectIdFromConversation,
  isConversationHiddenBecauseRestituted,
  isConversationInRestoredInbox,
} from '../utils/foundObjectConversation';
import { formatFrenchDateTimeInline } from '../utils/formatDateTimeFr';
import {
  ConversationAvatar,
  getConversationListButtonSx,
} from '../components/conversation/ConversationChatUi';

function firstImageUrl(images?: string[] | null): string | null {
  const p = images?.find((x) => x && String(x).trim());
  return p ? getAnnouncementImageUrl(p) : null;
}

const MessagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const conversationParam = searchParams.get('conversation');
  const announcementParam = searchParams.get('announcement');
  const foundObjectParam = searchParams.get('found_object');

  const selectedId = conversationParam ? Number.parseInt(conversationParam, 10) : null;
  const [startError, setStartError] = useState<string | null>(null);
  const [startingConversation, setStartingConversation] = useState(false);
  const openingRef = useRef(false);
  const [listFilter, setListFilter] = useState<'pending' | 'unread' | 'all' | 'archived' | 'restored'>('all');
  const [sortOrder, setSortOrder] = useState<'recent' | 'oldest'>('oldest');
  const [searchText, setSearchText] = useState('');
  const [actionSnackbar, setActionSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [rowActionId, setRowActionId] = useState<number | null>(null);

  const showArchived = listFilter === 'archived';
  const showRestored = listFilter === 'restored';
  const { data: convData, isLoading: loadingList } = useConversationsListQuery({
    poll: true,
    archived: showArchived,
    restored: showRestored,
  });

  const {
    data: detailData,
    isLoading: loadingDetail,
    error: detailError,
  } = useConversationDetailQuery({
    conversationId: selectedId,
    poll: true,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) =>
      showArchived ? apiService.unarchiveConversation(id) : apiService.archiveConversation(id),
    onMutate: (id) => {
      setRowActionId(id);
    },
    onSuccess: (res, id) => {
      setRowActionId(null);
      if (selectedId === id) {
        setSearchParams({});
      }
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setRowActionId(null);
      setActionSnackbar({
        open: true,
        message: showArchived
          ? 'Impossible de désarchiver la conversation.'
          : 'Impossible d’archiver la conversation.',
        severity: 'error',
      });
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: (id: number) => apiService.markConversationUnread(id),
    onMutate: (id) => {
      setRowActionId(id);
    },
    onSuccess: (res, id) => {
      setRowActionId(null);
      if (selectedId === id) {
        setSearchParams({});
      }
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setRowActionId(null);
      setActionSnackbar({
        open: true,
        message: 'Impossible de marquer la conversation comme non lue.',
        severity: 'error',
      });
    },
  });

  const selectedFoundObjectId = foundObjectParam ? Number.parseInt(foundObjectParam, 10) : null;

  useEffect(() => {
    const aid = announcementParam ? Number.parseInt(announcementParam, 10) : null;
    if (!aid || Number.isNaN(aid) || conversationParam) return;
    if (openingRef.current) return;
    openingRef.current = true;
    setStartingConversation(true);
    setStartError(null);
    apiService
      .startConversation(aid)
      .then((data) => {
        openingRef.current = false;
        setStartingConversation(false);
        setSearchParams({ conversation: String(data.conversation_id) });
        void invalidateMessagingCaches(queryClient);
        queryClient.invalidateQueries({ queryKey: ['conversation', data.conversation_id] });
      })
      .catch((err: any) => {
        openingRef.current = false;
        setStartingConversation(false);
        setStartError(err?.response?.data?.error || 'Impossible de démarrer la conversation.');
      });
  }, [announcementParam, conversationParam, setSearchParams, queryClient]);

  const conversations = useMemo(() => {
    const all = convData?.conversations ?? [];
    if (showRestored) {
      return all.filter((c: any) => isConversationInRestoredInbox(c));
    }
    return all.filter((c: any) => !isConversationHiddenBecauseRestituted(c));
  }, [convData?.conversations, showRestored]);
  const totalUnread = conversations.reduce(
    (acc: number, c: any) => acc + (c.unread_count ?? 0),
    0
  );
  const pendingCount = conversations.filter((c: any) => c.last_message && !c.last_message.is_from_me).length;
  const unreadCount = conversations.filter((c: any) => (c.unread_count ?? 0) > 0).length;

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    let result = [...conversations];

    if (listFilter === 'pending') {
      result = result.filter((c: any) => c.last_message && !c.last_message.is_from_me);
    } else if (listFilter === 'unread') {
      result = result.filter((c: any) => (c.unread_count ?? 0) > 0);
    }
    // listFilter === 'archived' | 'restored' | 'all' : pas de filtre supplémentaire

    if (normalizedSearch) {
      result = result.filter((c: any) => {
        const fullName = `${c.other_user?.first_name ?? ''} ${c.other_user?.last_name ?? ''}`.toLowerCase();
        const title = String(c.announcement?.title ?? '').toLowerCase();
        const preview = String(c.last_message?.content ?? '').toLowerCase();
        const status = String(c.announcement?.found_object_status ?? '').toLowerCase();
        return (
          fullName.includes(normalizedSearch) ||
          title.includes(normalizedSearch) ||
          preview.includes(normalizedSearch) ||
          status.includes(normalizedSearch)
        );
      });
    }

    result.sort((a: any, b: any) => {
      const ta = new Date(a.updated_at ?? 0).getTime();
      const tb = new Date(b.updated_at ?? 0).getTime();
      return sortOrder === 'recent' ? tb - ta : ta - tb;
    });

    return result;
  }, [conversations, listFilter, searchText, sortOrder]);

  const inferredFoundObjectIdFromConversation = useMemo(() => {
    const attrs = detailData?.conversation?.announcement?.object_attributes;
    const fromAttrs = Number(attrs?.found_object_id);
    if (Number.isFinite(fromAttrs) && fromAttrs > 0) return fromAttrs;

    const ref = String(detailData?.conversation?.announcement?.reference_code ?? '');
    const match = ref.match(/^FO-CONTACT-(\d+)$/);
    if (match?.[1]) return Number.parseInt(match[1], 10);
    return null;
  }, [detailData]);

  const activeFoundObjectId = selectedFoundObjectId ?? inferredFoundObjectIdFromConversation;

  /** Complète l’URL avec `found_object` une fois la conversation chargée (liens directs `?conversation=`). */
  useEffect(() => {
    if (!selectedId || Number.isNaN(selectedId) || foundObjectParam) return;
    const inferred = inferredFoundObjectIdFromConversation;
    if (inferred != null && inferred > 0) {
      setSearchParams(
        { conversation: String(selectedId), found_object: String(inferred) },
        { replace: true }
      );
    }
  }, [selectedId, foundObjectParam, inferredFoundObjectIdFromConversation, setSearchParams]);

  const isObjectMessagingView = Boolean(
    (activeFoundObjectId != null && activeFoundObjectId > 0) || announcementParam
  );

  const handleSelect = (id: number) => {
    const c = conversations.find((x: any) => x.id === id);
    const foId = getFoundObjectIdFromConversation(c);
    const nextParams: Record<string, string> = { conversation: String(id) };
    if (foId != null) {
      nextParams.found_object = String(foId);
    }
    setSearchParams(nextParams);
  };

  const handleWorkspaceConversation = (id: number | null) => {
    if (id == null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('conversation');
        return next;
      });
      return;
    }
    handleSelect(id);
  };

  const closeMessagingDetail = () => {
    setSearchParams({});
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 0, md: 2 },
        flex: { xs: 1, md: 'none' },
        minHeight: { xs: 0, md: 480 },
        height: { xs: '100%', md: 'auto' },
        maxWidth: '100%',
        width: '100%',
      }}
    >
      {!isObjectMessagingView && (
      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          flex: { xs: 1, md: 'none' },
          minHeight: { xs: 0, md: 'auto' },
          height: { xs: '100%', md: 'auto' },
          maxHeight: { xs: 'none', md: '78vh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: { xs: 0, md: 2 },
          boxShadow: { xs: 'none', md: undefined },
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Messagerie
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {totalUnread > 0 ? `${totalUnread} non lu(s)` : 'À jour'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, rowGap: 1 }}>
            <Chip
              label={`En attente de réponse ${pendingCount}`}
              color={listFilter === 'pending' ? 'warning' : 'default'}
              variant={listFilter === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('pending')}
              clickable
              disabled={showArchived || showRestored}
            />
            <Chip
              label={`Non lus ${unreadCount}`}
              color={listFilter === 'unread' ? 'error' : 'default'}
              variant={listFilter === 'unread' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('unread')}
              clickable
              disabled={showArchived || showRestored}
            />
            <Chip
              label={showArchived || showRestored ? 'Tous' : `Tous ${conversations.length}`}
              color={listFilter === 'all' ? 'primary' : 'default'}
              variant={listFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('all')}
              clickable
            />
            <Chip
              label={showRestored ? `Restitués ${conversations.length}` : 'Restitués'}
              color={listFilter === 'restored' ? 'success' : 'default'}
              variant={listFilter === 'restored' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('restored')}
              clickable
            />
            <Chip
              label={showArchived ? `Archivées ${conversations.length}` : 'Archivées'}
              color={listFilter === 'archived' ? 'secondary' : 'default'}
              variant={listFilter === 'archived' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('archived')}
              clickable
            />
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Trier par :
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={sortOrder}
              onChange={(_, value) => value && setSortOrder(value)}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              <ToggleButton value="recent">Plus récent</ToggleButton>
              <ToggleButton value="oldest">Plus ancien</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher dans les messages..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Box>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flex: 1 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {showArchived
                ? 'Aucune conversation archivée.'
                : showRestored
                  ? 'Aucune conversation restituée ou en livraison.'
                  : 'Aucun message pour ce filtre.'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 1, flex: 1, minHeight: 0, overflow: 'auto' }}>
            {filteredConversations.map((c: any) => {
              const rowBusy = rowActionId === c.id;
              return (
              <ListItem
                key={c.id}
                disablePadding
                sx={{ mb: 0.75 }}
                secondaryAction={
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ pr: 0.5 }}>
                    <Tooltip title="Marquer comme non lu">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Marquer comme non lu"
                          disabled={rowBusy || markUnreadMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            markUnreadMutation.mutate(c.id);
                          }}
                        >
                          <MarkEmailUnreadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={showArchived ? 'Désarchiver' : 'Archiver'}>
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={showArchived ? 'Désarchiver' : 'Archiver'}
                          disabled={rowBusy || archiveMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveMutation.mutate(c.id);
                          }}
                        >
                          {showArchived ? (
                            <UnarchiveIcon fontSize="small" />
                          ) : (
                            <ArchiveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemButton
                  selected={selectedId === c.id}
                  onClick={() => handleSelect(c.id)}
                  sx={{
                    borderRadius: 2,
                    alignItems: 'flex-start',
                    pr: { xs: 10, sm: 12 },
                    ...getConversationListButtonSx(c.id, selectedId === c.id),
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!c.unread_count || c.unread_count === 0}
                      overlap="circular"
                    >
                      <ConversationAvatar
                        conversationId={c.id}
                        imageSrc={firstImageUrl(c.announcement?.images)}
                        initial={c.other_user?.first_name}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {c.other_user ? `${c.other_user.first_name} ${c.other_user.last_name}` : 'Utilisateur'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatFrenchDateTimeInline(
                            c.last_message?.created_at ?? c.updated_at
                          )}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {c.announcement?.title || 'Annonce'}
                          {showRestored && c.announcement?.found_object_status
                            ? ` · ${
                                c.announcement.found_object_status === 'claimed'
                                  ? 'Restitué'
                                  : c.announcement.found_object_status === 'in_transit'
                                    ? 'En livraison'
                                    : c.announcement.found_object_status === 'to_ship'
                                      ? 'À expédier'
                                      : c.announcement.found_object_status
                              }`
                            : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {c.last_message?.content || '…'}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        )}
      </Card>
      )}

      <Snackbar
        open={actionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setActionSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={actionSnackbar.severity}
          onClose={() => setActionSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {actionSnackbar.message}
        </Alert>
      </Snackbar>
      {isObjectMessagingView && activeFoundObjectId != null && activeFoundObjectId > 0 && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal - 1,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <FoundObjectMessagesWorkspace
              variant="fullscreen"
              foundObjectId={activeFoundObjectId}
              selectedConversationId={selectedId}
              onSelectConversation={handleWorkspaceConversation}
              onClose={closeMessagingDetail}
              preferArchivedList={showArchived}
              preferRestoredList={showRestored}
              onRequestEditFoundObject={() => {
                navigate(`/found-objects?open=${activeFoundObjectId}`);
              }}
              announcementFlow={!!announcementParam}
              conversationPresentInUrl={!!conversationParam}
              startingConversation={startingConversation}
              startError={startError}
              onDismissStartError={() => setStartError(null)}
              onHandoverConfirmed={closeMessagingDetail}
            />
          </Box>
        </Box>
      )}
      {isObjectMessagingView &&
        (!activeFoundObjectId || activeFoundObjectId <= 0) &&
        announcementParam &&
        !conversationParam && (
          <Card sx={{ p: 3, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} />
              <Typography>Préparation de la conversation…</Typography>
            </Box>
          </Card>
        )}
    </Box>
  );
};

export default MessagesPage;
